import { useCallback, useState, type FormEvent } from 'react';
import { getLodgeAnniversaryYears } from '../../lib/lodgeAnniversary';
import { PublicLayout } from '../PublicLayout';
import { TurnstileWidget } from '../TurnstileWidget';

export function HomePage() {
  const anniversaryYears = getLodgeAnniversaryYears();
  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [contactNotice, setContactNotice] = useState('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);

  async function submitContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setContactNotice('');
    setIsSubmittingContact(true);

    try {
      const response = await fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ ...contact, website, turnstileToken })
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Please wait before sending another message.');
        }
        throw new Error('Unable to send your message right now.');
      }

      setContact({ name: '', email: '', message: '' });
      setWebsite('');
      setContactNotice('Thank you. Your message has been received by the Lodge.');
    } catch (error) {
      setContactNotice(error instanceof Error ? error.message : 'Unable to send your message right now.');
    } finally {
      setTurnstileToken('');
      setTurnstileResetSignal((current) => current + 1);
      setIsSubmittingContact(false);
    }
  }

  return (
    <PublicLayout>
      <main id="top">
        <section className="hero-page">
          <div className="orb one" />
          <div className="orb two" />
          <div className="hero-copy">
            <span className="eyebrow">{anniversaryYears} years in Lawrenceville</span>
            <h1>Brotherly Love. <span>Relief. Truth.</span></h1>
            <p className="lead">Lawrenceville Lodge No. 131 is a Masonic lodge serving Lawrenceville through fellowship, character, and service. To be one, ask one.</p>
            <div className="hero-actions">
              <a className="button-primary" href="#join">2B1 ASK1</a>
              <a className="button-secondary" href="tel:+17709631726">Call the Lodge</a>
              <div className="meeting-chip" aria-label="Meeting schedule">
                <small>Stated Communications</small>
                <strong>1st &amp; 3rd Tuesdays · Dinner 6:30 PM · Lodge 7:30 PM</strong>
              </div>
            </div>
          </div>
          <aside className="hero-panel" aria-label="Masonic lodge imagery">
            <div className="hero-card-inner">
              <div className="main-photo" />
              <svg className="hero-emblem" viewBox="0 0 300 300" role="img" aria-label="Square and compasses emblem illustration">
                <defs>
                  <linearGradient id="heroGold" x1="0" x2="1">
                    <stop offset="0" stopColor="#fff0a5" />
                    <stop offset=".45" stopColor="#d6ad4f" />
                    <stop offset="1" stopColor="#8c6a20" />
                  </linearGradient>
                </defs>
                <path d="M150 22 L222 244 L198 244 L150 96 L102 244 L78 244 Z" fill="url(#heroGold)" />
                <path d="M58 130 L150 222 L242 130 L258 146 L150 254 L42 146 Z" fill="none" stroke="url(#heroGold)" strokeWidth="24" strokeLinejoin="round" />
                <circle cx="150" cy="48" r="25" fill="#07132a" stroke="url(#heroGold)" strokeWidth="13" />
                <text x="150" y="188" textAnchor="middle" fontSize="86" fontFamily="Georgia" fontWeight="800" fill="url(#heroGold)">G</text>
              </svg>
              <div className="hero-info">
                <h2>Square. Compass. G.</h2>
                <p>For {anniversaryYears} years, Lawrenceville Lodge No. 131 has carried the work of fellowship, character, and Masonic light in Gwinnett County.</p>
              </div>
            </div>
          </aside>
        </section>

        <section id="values">
          <div className="wrap">
            <div className="section-head">
              <h2>Three lights for practical living.</h2>
              <p>The public work of the Lodge is simple: build character, strengthen fellowship, and serve Lawrenceville with dignity.</p>
            </div>
            <div className="grid-3">
              <article className="card">
                <div className="icon">✦</div>
                <h3>Brotherly Love</h3>
                <p>A Lodge is not a club of convenience. It is a disciplined fraternity where men learn respect, accountability, and friendship across age, background, and station.</p>
              </article>
              <article className="card">
                <div className="icon">◆</div>
                <h3>Relief</h3>
                <p>Charity is more than donation. It is attention, presence, and service: supporting Brothers, families, widows, and the surrounding community.</p>
              </article>
              <article className="card">
                <div className="icon">☉</div>
                <h3>Truth</h3>
                <p>Masonic education points a man toward self-examination, honest conduct, reverence, and a life shaped by principle rather than impulse.</p>
              </article>
            </div>
          </div>
        </section>

        <section id="heritage">
          <div className="wrap timeline">
            <div className="photo-tile">
              <div className="photo-caption">{anniversaryYears} years of Masonic tradition in Lawrenceville: historical, fraternal, and civic.</div>
            </div>
            <div className="steps">
              <div className="section-head compact">
                <h2>{anniversaryYears} years rooted in Lawrenceville.</h2>
              </div>
              <article className="step">
                <div className="num">01</div>
                <div>
                  <h3>A Blue Lodge with local identity</h3>
                  <p>Lawrenceville Lodge No. 131 is a fraternal organization of Free & Accepted Masons serving Lawrenceville and the surrounding community.</p>
                </div>
              </article>
              <article className="step">
                <div className="num">02</div>
                <div>
                  <h3>Meeting on the level</h3>
                  <p>Members gather for stated communications, fellowship, education, and service. Visiting Brothers are welcome according to regular Masonic practice.</p>
                </div>
              </article>
              <article className="step">
                <div className="num">03</div>
                <div>
                  <h3>A path for seekers</h3>
                  <p>Good men seeking light are invited to make contact, ask questions, and begin the petition process when ready.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="join" className="ask-section">
          <div className="wrap">
            <div className="ask-box">
              <div className="ask-content">
                <svg className="ask-emblem" viewBox="0 0 300 300" aria-hidden="true">
                  <defs>
                    <linearGradient id="askGold" x1="0" x2="1">
                      <stop offset="0" stopColor="#fff0a5" />
                      <stop offset=".45" stopColor="#d6ad4f" />
                      <stop offset="1" stopColor="#8c6a20" />
                    </linearGradient>
                  </defs>
                  <path d="M150 22 L222 244 L198 244 L150 96 L102 244 L78 244 Z" fill="url(#askGold)" />
                  <path d="M58 130 L150 222 L242 130 L258 146 L150 254 L42 146 Z" fill="none" stroke="url(#askGold)" strokeWidth="24" strokeLinejoin="round" />
                  <circle cx="150" cy="48" r="25" fill="#0b2449" stroke="url(#askGold)" strokeWidth="13" />
                  <text x="150" y="188" textAnchor="middle" fontSize="86" fontFamily="Georgia" fontWeight="800" fill="url(#askGold)">G</text>
                </svg>
                <span className="eyebrow">2B1 ASK1</span>
                <h2>Interested in Freemasonry?</h2>
                <p>Freemasonry does not chase men into membership. The first step is yours. Ask a Mason, come to dinner, speak with the Lodge, and learn the qualifications, expectations, and petition process.</p>
                <div className="hero-actions">
                  <a className="button-primary" href="#contact">Start the Conversation</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="events">
          <div className="wrap">
            <div className="section-head">
              <h2>Meetings & fellowship.</h2>
              <p>Use this section for stated communications, open dinners, degree work notices, service projects, and education nights.</p>
            </div>
            <div className="events">
              <article className="event">
                <span>1st &amp; 3rd Tuesdays</span>
                <strong>Stated Communications</strong>
                <p>Dinner at 6:30 PM. Lodge opens at 7:30 PM. Visiting Brothers should bring proper credentials.</p>
              </article>
              <article className="event">
                <span>Open invitation</span>
                <strong>Prospective members</strong>
                <p>Curious men may contact the Lodge and ask about attending dinner before a meeting to learn more.</p>
              </article>
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="wrap contact-grid">
            <div className="contact-card">
              <h2>Visit or contact the Lodge</h2>
              <p><strong>Lawrenceville Masonic Lodge No. 131</strong><br />455 S Perry St<br />Lawrenceville, GA</p>
              <ul>
                <li><a href="tel:+17709631726">(770) 963-1726</a></li>
                <li>Parking and wheelchair-accessible onsite services are listed publicly.</li>
              </ul>
            </div>
            <div className="contact-card">
              <h2>Ask a question</h2>
              <form onSubmit={submitContact}>
                <input name="name" placeholder="Name" autoComplete="name" value={contact.name} onChange={(event) => setContact({ ...contact, name: event.target.value })} required />
                <input name="email" placeholder="Email" type="email" autoComplete="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} required />
                <textarea name="message" placeholder="I am interested in learning more about Lawrenceville Lodge No. 131..." value={contact.message} onChange={(event) => setContact({ ...contact, message: event.target.value })} required />
                <input
                  className="contact-honeypot"
                  name="website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  aria-hidden="true"
                  tabIndex={-1}
                  autoComplete="off"
                />
                {turnstileSiteKey ? (
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    resetSignal={turnstileResetSignal}
                    onToken={handleTurnstileToken}
                  />
                ) : (
                  <p className="contact-verification-error">Contact verification is temporarily unavailable.</p>
                )}
                <button className="button-primary" type="submit" disabled={isSubmittingContact || !turnstileToken}>{isSubmittingContact ? 'Sending…' : 'Send Inquiry'}</button>
                {contactNotice ? <p role="status">{contactNotice}</p> : null}
              </form>
            </div>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
}
